/**
 * HeroMind PWA v7 — entry point.
 */

import './styles/base.css';
import './components/theme.js';   // Apply theme from localStorage on load

// Preload UX components so window.* helpers are immediately callable
import './components/toast.js';
import './components/confetti.js';
import './components/achievement.js';
import './components/sounds.js';
import './components/pin-modal.js';
import './components/modal.js';
import './components/tutorial.js';

import './core/firebase.js';
import { enableOfflinePersistence } from './core/firebase.js';
import { state, subscribe } from './core/state.js';
import { register, start, navigate } from './core/router.js';
import { applyLang } from './core/i18n.js';
import { toast } from './components/toast.js';
import { onAuthChange } from './modules/auth/index.js';
import { listenForegroundMessages, registerCurrentUser } from './modules/notifications/fcm.js';
import { APP_VERSION } from './core/version.js';
import { silentBootCheck } from './components/update-check.js';
import { startFamilyListeners, stopAllListeners } from './core/listeners.js';
import { fb } from './core/firebase.js';
import { metaRef, heroesCol } from './core/paths.js';

enableOfflinePersistence();

// ── Route table (lazy chunks) ───────────────────────────────────
register('auth',                () => import('./modules/auth/screen.js'));
register('onboarding',          () => import('./modules/auth/onboarding.js'));
register('hero-select',         () => import('./modules/auth/hero-select.js'));
register('hero',                () => import('./modules/hero/dashboard.js'));
register('shop',                () => import('./modules/hero/shop.js'));
register('calendar',            () => import('./modules/hero/calendar.js'));
register('admin',               () => import('./modules/admin/index.js'));
register('coach',               () => import('./modules/admin/coach-ia.js'));
register('reports',             () => import('./modules/reports/index.js'));
register('catalog',             () => import('./modules/catalog/list.js'));
register('catalog-detail',      () => import('./modules/catalog/detail.js'));
register('wellness',            () => import('./modules/wellness/dashboard.js'));
register('subscription',        () => import('./modules/subscriptions/plans.js'));
register('professional-dashboard', () => import('./modules/professional/dashboard.js'));

// ── Auth bootstrap ───────────────────────────────────────────────
onAuthChange(async (user) => {
  if (!user) {
    stopAllListeners();
    state.isHeroMode = false;
    state.selectedHeroId = null;
    document.body.classList.remove('hero-mode');
    navigate('auth');
    return;
  }

  // Start family listeners
  startFamilyListeners();

  // Wait for meta + heroes to load (one round-trip)
  try {
    const [metaSnap, heroesSnap] = await Promise.all([
      fb.getDoc(metaRef()),
      fb.getDocs(heroesCol()),
    ]);

    const meta = metaSnap.exists() ? metaSnap.data() : null;
    const heroes = heroesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (!meta || meta.setupComplete === false || heroes.length === 0) {
      navigate('onboarding');
    } else {
      navigate('hero-select');
    }

    // Register FCM in background (non-blocking)
    registerCurrentUser().catch(() => {});
  } catch (e) {
    console.error('[boot] resolve failed', e);
    navigate('hero-select'); // fallback
  }
});

// ── Foreground FCM toasts ────────────────────────────────────────
listenForegroundMessages();

// ── Sync status & connectivity ───────────────────────────────────
window.addEventListener('online',  () => { state.syncStatus = 'idle'; toast('Conectado', { type: 'success' }); });
window.addEventListener('offline', () => { state.syncStatus = 'offline'; toast('Sin conexión', { type: 'warning' }); });

// ── Sync indicator text ──────────────────────────────────────────
subscribe('syncStatus', (status) => {
  const el = document.getElementById('sync-indicator');
  if (!el) return;
  el.dataset.status = status;
  el.textContent = { idle: 'ONLINE', syncing: 'SYNC…', offline: 'OFFLINE' }[status] || '';
});

// ── Start router ─────────────────────────────────────────────────
start('auth');
applyLang();
console.log(`[HeroMind] v${APP_VERSION}`);
setTimeout(() => silentBootCheck().catch(() => {}), 3000);

if (import.meta.env.DEV) {
  window.__heromind = { state, navigate, toast };
  console.log('[HeroMind v7] dev mode — state at window.__heromind.state');
}
